import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Rocket, Heart, Lightbulb, Eye, EyeOff, Check, AlertCircle } from "lucide-react";
import { register, setAccessToken as setAuthToken } from "../services/authService";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox"; 
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';
import { validatePassword, validateFullName, validateEmail } from "../utils/validation";

// Add this CSS at the top of your file or in your global styles
const passwordRequirementsStyle = {
  position: 'absolute',
  right: '-240px',
  top: '0',
  width: '220px',
  padding: '8px',
  backgroundColor: 'white',
  borderRadius: '6px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e2e8f0',
  zIndex: 50,
} as const;

const SignIn = () => {
  const { signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || location.state?.redirectTo || '/';
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
  });

  // Add new state for validation
  const [validationErrors, setValidationErrors] = useState({
    fullName: "",
    email: "",
    password: "",
  });
  
  // Add state for password requirements visibility
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Add state for password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  // Add function to check if password is valid
  const isPasswordValid = () => {
    const { isValid } = validatePassword(formData.password);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const passwordValidation = validatePassword(formData.password);
    const nameValidation = validateFullName(formData.fullName);
    const emailValidation = validateEmail(formData.email);

    const newValidationErrors = {
      password: !passwordValidation.isValid ? "Password does not meet requirements" : "",
      fullName: nameValidation.error,
      email: !emailValidation ? "Please enter a valid email address" : "",
    };

    setValidationErrors(newValidationErrors);

    // Check if there are any validation errors
    if (Object.values(newValidationErrors).some(error => error !== "")) {
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await register(formData);
      
      // Explicitly log the response for debugging
      console.log('Registration response:', response);
      
      // Always set the access token if present in response
      if (response.accessToken) {
        console.log('Setting access token from registration response');
        setAuthToken(response.accessToken);
        
        // Force update to auth state
        window.dispatchEvent(new Event('tokenChange'));
      }
      
      if (response.emailVerified) {
        // User is registered and email is verified
        toast.success("Registration successful!");
        
        // Add a small delay to ensure auth state has updated
        setTimeout(() => {
          // Redirect to the page they were trying to access if it exists
          if (location.state?.from) {
            navigate(location.state.from);
          } else {
            navigate('/profile/edit');
          }
        }, 100);
      } else {
        // Navigate to verification screen
        toast.success(response.message || "Registration successful! Please verify your email.");
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Extract error message from response if available
      let errorMessage = "Registration failed. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 409) {
        errorMessage = "An account with this email already exists.";
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid registration data. Please check your inputs.";
      } else if (error.response?.status === 422) {
        errorMessage = "Email or password format is invalid.";
      }
      
      toast.error(errorMessage);
      
      // Handle specific validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        setValidationErrors(prev => ({
          ...prev,
          ...Object.keys(backendErrors).reduce((acc, key) => ({
            ...acc,
            [key]: backendErrors[key].join(', ')
          }), {})
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // Save current location for redirect after login
      const currentPath = location.state?.from || '/';
      localStorage.setItem('redirect_after_login', currentPath);
      
      // Mark this as a new registration
      localStorage.setItem('isNewRegistration', 'true');
      
      // Construct the OAuth URL with redirect_uri that handles both production and development
      const baseUrl = config.baseUrl || window.location.origin;
      const redirectUri = encodeURIComponent(`${baseUrl}/auth/callback`);
      const googleAuthUrl = `${config.oauthUrl}/authorization/google?redirect_uri=${redirectUri}`;
      
      // Debug logging
      console.log('Base URL:', baseUrl);
      console.log('Redirect URI:', redirectUri);
      console.log('Google Auth URL:', googleAuthUrl);
      
      // Redirect to Google OAuth
      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error("Google sign-in failed");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate on change
    if (name === 'password') {
      const { isValid, requirements } = validatePassword(value);
      if (!isValid) {
        let errorMessage = "";
        if (!requirements.minLength) errorMessage = "Password must be at least 8 characters long";
        else if (!requirements.hasUpperCase) errorMessage = "Password must contain at least one uppercase letter";
        else if (!requirements.hasLowerCase) errorMessage = "Password must contain at least one lowercase letter";
        else if (!requirements.hasNumber) errorMessage = "Password must contain at least one number";
        else if (!requirements.hasSpecialChar) errorMessage = "Password must contain at least one special character";
        
        setValidationErrors(prev => ({
          ...prev,
          password: errorMessage
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          password: ""
        }));
      }
    }

    if (name === 'fullName') {
      const { isValid, error } = validateFullName(value);
      setValidationErrors(prev => ({
        ...prev,
        fullName: error
      }));
    }

    if (name === 'email') {
      const isValid = validateEmail(value);
      setValidationErrors(prev => ({
        ...prev,
        email: isValid ? "" : "Please enter a valid email address"
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      
      <div className="container mx-auto px-4 pt-16 pb-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              Create your account
            </h1>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Be a Wanter</strong>
                Share your will with others and get perfectly matched offers to accomplish your desires.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Lightbulb className="h-5 w-5 text-yellow-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Do What You Love</strong>
                Create offers and start fulfilling others' desires!
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Rocket className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Early Access Benefits</strong>
                Want to be the first to see desires that match your skill set? Contact us!
              </p>
            </div>
          </div>

          <Button
            onClick={handleGoogleSignIn}
            className="w-full mb-4 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>

          <div className="relative mb-4">
            <Separator className="my-4" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-white px-2 text-gray-500 text-sm">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleChange}
                className={validationErrors.fullName ? "border-red-500" : ""}
              />
              {validationErrors.fullName && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.fullName}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={validationErrors.email ? "border-red-500" : ""}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div className="space-y-1.5 relative">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setShowPasswordRequirements(true)}
                  onBlur={(e) => {
                    if (!e.relatedTarget?.closest('.password-requirements')) {
                      setShowPasswordRequirements(false);
                    }
                  }}
                  className={`pr-16 ${validationErrors.password ? "border-red-500" : ""}`}
                />
                <div className="absolute right-0 top-0 h-full flex items-center gap-1 pr-3">
                  {formData.password && (
                    <button
                      type="button"
                      onClick={() => setShowPasswordRequirements(true)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      {isPasswordValid() ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
                {showPasswordRequirements && (
                  <div 
                    className="password-requirements"
                    style={passwordRequirementsStyle}
                    onMouseLeave={() => setShowPasswordRequirements(false)}
                  >
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${formData.password.length >= 8 ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span>8+ characters</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${/[A-Z]/.test(formData.password) ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span>Uppercase letter</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${/[a-z]/.test(formData.password) ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span>Lowercase letter</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${/[0-9]/.test(formData.password) ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span>Number</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span>Special character</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {validationErrors.password && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              People who use our service may have uploaded your contact information to WANTTD. <Link to="/learn-more" className="text-blue-600 hover:underline">Learn More</Link>
              <br />
              By signing up, you agree to our <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</Link>, <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</Link>, and <Link to="/cookies" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Cookies Policy</Link>.
            </p>

            <Button
              type="submit"
              disabled={
                isLoading || 
                !!validationErrors.email || 
                !!validationErrors.password || 
                !!validationErrors.fullName ||
                !formData.email ||
                !formData.password ||
                !formData.fullName
              }
              className="w-full bg-black text-white hover:bg-black/90"
            >
              {isLoading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <div className="mt-4 text-center pb-4">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 hover:text-blue-700">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
